import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, RefreshCw, Coins, FileWarning, AlertCircle, Banknote } from 'lucide-react';
import { Input, Select } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { RevenuesTable } from '../../components/finance/RevenuesTable';
import { RevenueFormModal } from '../../components/finance/RevenueFormModal';
import { RevenueDetailModal } from '../../components/finance/RevenueDetailModal';
import { useRevenues } from '../../hooks/useRevenues';
import { useCustomers } from '../../hooks/useCustomers';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import { useAuth } from '../../context/AuthContext';
import type { Revenue } from '../../types/finance';
import type { RevenueCreateDto } from '../../lib/api/endpoints';
import { uploadRevenueAttachment, getRevenue } from '../../lib/api/endpoints';
import { revenueFromDto } from '../../lib/api/mappers';
import { comprimirRecibo } from '../../utils/receiptImage';
import { EXPENSE_DATE_FILTERS, rangoDeFiltro, sumarMontos, formatearCOP, type ExpenseDateFilter } from '../../utils/expenses';
import { puedeRegistrarIngreso, puedeCorregirIngreso, dentroDeVentanaEdicionIngreso, sinSoporte, cobradoDistintoDelPropuesto } from '../../utils/finance';

/**
 * FE-32 — la pantalla de ingresos (fletes, servicios). Es el espejo de la de
 * gastos: el servidor acota la visibilidad por rol (el Operador ve los suyos),
 * el registro son dos llamadas (ingreso y después la foto del tiquete), y la
 * suma de arriba es de lo que se está mirando.
 *
 * Lo que es propio: el tile «cobrado ≠ propuesto», que cuenta cuántos ingresos
 * del periodo se cobraron por un importe distinto del que la tarifa decía. En
 * el cuaderno del dueño eran 44 de 88 y nadie lo sabía.
 */
export default function RevenuesPage() {
  const { user } = useAuth();
  // Finanzas enlaza aquí con `?customerId=&q=&periodo=` (clic en una ruta):
  // los filtros arrancan con eso y después se manejan como siempre.
  const [params] = useSearchParams();
  const [dateFilter, setDateFilter] = useState<ExpenseDateFilter>(() => (params.get('periodo') as ExpenseDateFilter | null) ?? 'month');
  const [customerFilter, setCustomerFilter] = useState(() => params.get('customerId') ?? '');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [search, setSearch] = useState(() => params.get('q') ?? '');

  const filtro = useMemo(() => ({
    ...rangoDeFiltro(dateFilter),
    customerId: customerFilter || undefined,
    vehicleId: vehicleFilter || undefined,
  }), [dateFilter, customerFilter, vehicleFilter]);

  const { revenues, isLoading, error, reload, registrar, corregir, reemplazar } = useRevenues(filtro);
  const { customers } = useCustomers();
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();

  const [formAbierto, setFormAbierto] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [corrigiendoId, setCorrigiendoId] = useState<string | null>(null);
  const [avisoSoporte, setAvisoSoporte] = useState<string | null>(null);

  useEffect(() => { document.title = 'Ingresos | Paitron Radar'; }, []);

  const detalle = revenues.find(r => r.id === detalleId) ?? null;
  const corrigiendo = revenues.find(r => r.id === corrigiendoId) ?? null;

  const visibles = revenues.filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return r.customerName.toLowerCase().includes(q) || (r.tariffName ?? '').toLowerCase().includes(q)
      || (r.vehiclePlate ?? '').toLowerCase().includes(q) || (r.driverName ?? '').toLowerCase().includes(q)
      || (r.reference ?? '').toLowerCase().includes(q) || (r.notes ?? '').toLowerCase().includes(q);
  });
  const suma = sumarMontos(visibles.map(r => r.amount));
  const sinSoportes = visibles.filter(sinSoporte).length;
  const distintos = visibles.filter(cobradoDistintoDelPropuesto).length;

  const abrirCorreccion = (r: Revenue) => { setDetalleId(null); setCorrigiendoId(r.id); };

  const registrarConSoporte = async (data: RevenueCreateDto, soporte: File | null) => {
    setAvisoSoporte(null);
    const ingreso = await registrar(data);
    if (!soporte) return ingreso;
    try {
      const listo = await comprimirRecibo(soporte);
      await uploadRevenueAttachment(ingreso.id, listo);
      reemplazar(revenueFromDto(await getRevenue(ingreso.id)));
    } catch {
      setAvisoSoporte(`El ingreso de ${formatearCOP(ingreso.amount)} quedó registrado, pero la foto del tiquete no se pudo subir. Ábrelo y adjúntala desde su detalle.`);
    }
    return ingreso;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        {[
          { icon: <Banknote size={22} />, color: 'var(--accent)', valor: String(visibles.length), label: 'Ingresos en pantalla' },
          { icon: <Coins size={22} />, color: '#10B981', valor: formatearCOP(suma), label: 'Suma de lo filtrado' },
          { icon: <AlertCircle size={22} />, color: '#8B5CF6', valor: String(distintos), label: 'Cobrado ≠ propuesto' },
          { icon: <FileWarning size={22} />, color: '#F59E0B', valor: String(sinSoportes), label: 'Sin soporte' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: `${s.color}1F`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.icon}</div>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{s.valor}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px', minWidth: '180px' }}>
          <Input label="Buscar" placeholder="Cliente, tarifa, placa, referencia…" value={search} onChange={e => setSearch(e.target.value)} leftIcon={<Search size={15} />} />
        </div>
        <div style={{ flex: '0 1 170px', minWidth: '150px' }}>
          <Select label="Periodo" value={dateFilter} onChange={e => setDateFilter(e.target.value as ExpenseDateFilter)} options={EXPENSE_DATE_FILTERS.map(f => ({ value: f.id, label: f.label }))} />
        </div>
        <div style={{ flex: '0 1 190px', minWidth: '150px' }}>
          <Select label="Cliente" value={customerFilter} onChange={e => setCustomerFilter(e.target.value)}
            options={[{ value: '', label: 'Todos los clientes' }, ...customers.map(c => ({ value: c.id, label: c.active ? c.name : `${c.name} (inactivo)` }))]} />
        </div>
        <div style={{ flex: '0 1 170px', minWidth: '150px' }}>
          <Select label="Vehículo" value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)}
            options={[{ value: '', label: 'Todos los vehículos' }, ...vehicles.map(v => ({ value: v.id, label: v.plate }))]} />
        </div>
        <Button variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={reload} disabled={isLoading}>Actualizar</Button>
        {puedeRegistrarIngreso(user) && (
          <Button leftIcon={<Plus size={16} />} onClick={() => setFormAbierto(true)}>Registrar ingreso</Button>
        )}
      </div>

      {error && (
        <div style={{ padding: '0.875rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-lg)', color: '#EF4444', fontSize: '13px' }}>
          No se pudieron cargar los ingresos: {error.message}
        </div>
      )}
      {avisoSoporte && (
        <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start', padding: '0.875rem 1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '13px' }}>
          <FileWarning size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ flex: 1 }}>{avisoSoporte}</span>
          <button type="button" onClick={() => setAvisoSoporte(null)} style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 700 }}>Entendido</button>
        </div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>
      ) : visibles.length === 0 ? (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '2rem', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
          {search.trim() ? 'Ningún ingreso coincide con la búsqueda.' : 'Sin ingresos registrados en este periodo.'}
        </div>
      ) : (
        <RevenuesTable revenues={visibles} role={user?.role} onVerDetalle={r => setDetalleId(r.id)} onCorregir={abrirCorreccion} />
      )}

      <RevenueDetailModal
        revenue={detalle}
        onClose={() => setDetalleId(null)}
        onCorregir={abrirCorreccion}
        puedeCorregir={puedeCorregirIngreso(user)}
        ventanaVencida={detalle ? !dentroDeVentanaEdicionIngreso(detalle, user?.role) : false}
        puedeSubirSoporte={puedeRegistrarIngreso(user)}
        onSoporteSubido={reemplazar}
      />

      <RevenueFormModal
        isOpen={formAbierto || !!corrigiendo}
        onClose={() => { setFormAbierto(false); setCorrigiendoId(null); }}
        revenue={corrigiendo}
        customers={customers}
        vehicles={vehicles}
        drivers={drivers}
        driverAutomatico={user?.role === 'operator'}
        onRegistrar={registrarConSoporte}
        onCorregir={corregir}
      />
    </div>
  );
}
